import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n/context';

/*
 * שלושת כפתורי קבוצות השליטה.
 *
 * **הקשה שולפת, לחיצה ארוכה משייכת.** במקלדת יש Ctrl+ספרה, במגע אין מקש
 * מקביל — ולחיצה ארוכה היא הג'סטה היחידה שאינה גוזלת עוד משבצת בסרגל. אותו
 * סף זמן של הבחירה בקופסה (450ms), כדי שהאצבע תלמד תזמון אחד.
 *
 * הכפתור **אינו מושבת כשהקבוצה ריקה**: הוא עדיין היעד של לחיצה ארוכה לשיוך,
 * וכפתור מושבת אינו מקבל אירועי מצביע בכלל.
 */

const LONG_PRESS_MS = 450;

interface ControlGroupBarProps {
    /** כמה יחידות חיות בכל קבוצה — אורך המערך קובע כמה כפתורים. */
    readonly counts: readonly number[];
    readonly onRecall: (index: number) => void;
    readonly onAssign: (index: number) => void;
}

export function ControlGroupBar({ counts, onRecall, onAssign }: ControlGroupBarProps) {
    const { t } = useI18n();
    const timerRef = useRef<number | null>(null);
    const firedRef = useRef(false);
    const [held, setHeld] = useState(-1);

    const clear = useCallback(() => {
        if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        timerRef.current = null;
        setHeld(-1);
    }, []);

    // ניקוי בפירוק: טיימר פתוח היה קורא ל-setState אחרי שהרכיב כבר לא קיים.
    useEffect(() => clear, [clear]);

    const down = (index: number) => {
        firedRef.current = false;
        setHeld(index);
        timerRef.current = window.setTimeout(() => {
            firedRef.current = true;
            timerRef.current = null;
            setHeld(-1);
            onAssign(index);
        }, LONG_PRESS_MS);
    };

    const up = (index: number) => {
        // אם הלחיצה הארוכה כבר שייכה, השחרור אינו שולף גם — זו הקשה אחת.
        const assigned = firedRef.current;
        clear();
        if (!assigned) onRecall(index);
    };

    return (
        <div className="game__groups" role="group" aria-label={t('game_groups')}>
            {counts.map((count, index) => (
                <button
                    key={index}
                    type="button"
                    className={
                        held === index
                            ? 'game__groupButton game__groupButton--held'
                            : 'game__groupButton'
                    }
                    title={t('game_group_hint')}
                    aria-label={`${t('game_groups')} ${index + 1}`}
                    onPointerDown={() => down(index)}
                    onPointerUp={() => up(index)}
                    onPointerLeave={clear}
                    onPointerCancel={clear}
                    /*
                     * תפריט ההקשר של הדפדפן קופץ על לחיצה ארוכה במגע ובולע את
                     * הג'סטה. `-webkit-touch-callout` ב-CSS מכסה את iOS, וזה
                     * מכסה את השאר.
                     */
                    onContextMenu={(event) => event.preventDefault()}
                >
                    <span className="game__groupIndex num">{index + 1}</span>
                    {count > 0 && <span className="game__groupCount num">{count}</span>}
                </button>
            ))}
        </div>
    );
}
